/**
 * State a port definition: what crosses it, or - for a connector - its pins
 * (issue #52).
 *
 * Two sorts, asked for differently because they ARE different questions:
 *
 *   - A PORT FOR WHAT FLOWS - a telemetry port, a power input. The question is
 *     what crosses it and which way: `out item telemetry : HousekeepingTelemetry`.
 *     A row per thing, direction first, because the direction is what both sides
 *     of an interface have to agree about.
 *   - A CONNECTOR - an RJ-45, a 38999. The question is its family and its pins.
 *     A pin count gives pin1..pinN; a list names them (A1, A2, B1). A connector
 *     built on another connector inherits every pin and declares none - its pin
 *     FUNCTIONS are assigned afterwards, in its pinout.
 */

import { useState } from 'react'
import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/DeleteOutlined'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import IconButton from '@mui/material/IconButton'
import MenuItem from '@mui/material/MenuItem'
import Stack from '@mui/material/Stack'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import TextField from '@mui/material/TextField'
import ToggleButton from '@mui/material/ToggleButton'
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup'
import Typography from '@mui/material/Typography'
import { ApiError, api } from '../../api'
import type { FeatureDraft } from '../../schema'
import { useSchema } from '../../state/AppData'
import { useToasts } from '../../state/Toasts'
import { MONOSPACE } from '../../theme'
import ElementPicker from '../common/ElementPicker'
import { parsePins } from './icd-model'

type Sort = 'flow' | 'connector'

interface Row extends FeatureDraft {
  key: string
}

let rowCounter = 0
const emptyRow = (): Row => ({
  key: `feature-${rowCounter++}`, name: '', keyword: 'item', direction: 'out', type_name: '',
})

export default function PortDefWizard({
  onClose,
  onCreated,
  initialSort = 'flow',
}: {
  onClose: () => void
  onCreated: (identifier: string) => void
  initialSort?: Sort
}) {
  const schema = useSchema()
  const toasts = useToasts()
  const [sort, setSort] = useState<Sort>(initialSort)
  const [name, setName] = useState('')
  const [identifier, setIdentifier] = useState('')
  const [base, setBase] = useState('')
  const [statement, setStatement] = useState('')
  const [rows, setRows] = useState<Row[]>([emptyRow()])
  const [connectorType, setConnectorType] = useState('')
  const [pinText, setPinText] = useState('')
  const [busy, setBusy] = useState(false)
  const [failure, setFailure] = useState('')

  // A connector built on another connector inherits its pins.
  const inheritsPins = sort === 'connector' && Boolean(base)
  const { pins, problem } = parsePins(inheritsPins ? '' : pinText)
  const features = rows.filter((row) => row.name.trim())

  const set = (key: string, patch: Partial<Row>) =>
    setRows((current) => current.map((row) => (row.key === key ? { ...row, ...patch } : row)))

  const submit = async () => {
    setBusy(true)
    setFailure('')
    try {
      const made = await api.createPortDef({
        name: name.trim(),
        identifier: identifier.trim() || null,
        specializes: base,
        statement,
        ...(sort === 'flow'
          ? { features: features.map(({ key: _key, ...feature }) => ({ ...feature, name: feature.name.trim() })) }
          : { connector_type: connectorType.trim(), ...(pins.length ? { pins } : {}) }),
      })
      toasts.succeeded(
        `${made.identifier || made.name} created` + (made.pins.length ? ` with ${made.pins.length} pins` : ''),
      )
      onCreated(made.identifier || made.name)
    } catch (error) {
      setFailure((error as ApiError).message)
    } finally {
      setBusy(false)
    }
  }

  const ready = Boolean(name.trim()) && !problem && !busy &&
    (sort === 'flow' || inheritsPins || pins.length > 0 || Boolean(connectorType.trim()))

  return (
    <Dialog open onClose={busy ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle>New port definition</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {failure && <Alert severity="error">{failure}</Alert>}

          <ToggleButtonGroup
            exclusive
            size="small"
            value={sort}
            onChange={(_event, next: Sort | null) => next && setSort(next)}
            aria-label="What sort of port"
          >
            <ToggleButton value="flow">What flows through it</ToggleButton>
            <ToggleButton value="connector">A connector and its pins</ToggleButton>
          </ToggleButtonGroup>

          <Stack direction="row" spacing={2}>
            <TextField
              label="Name"
              required
              value={name}
              onChange={(event) => setName(event.target.value)}
              size="small"
              sx={{ flexGrow: 1 }}
              helperText={sort === 'connector' ? 'e.g. RJ45, or EthernetPort built on it' : 'e.g. TelemetryPort, PowerInPort'}
              slotProps={{ input: { sx: { fontFamily: MONOSPACE } } }}
            />
            <TextField
              label="ID"
              value={identifier}
              onChange={(event) => setIdentifier(event.target.value)}
              size="small"
              helperText="Optional"
              slotProps={{ input: { sx: { fontFamily: MONOSPACE } } }}
            />
          </Stack>

          <ElementPicker
            schema={schema}
            kind="interface"
            where={(row) => row.declared_as === 'port'}
            valueFor={(row) => String(row.qualified_name ?? row.id)}
            value={base || null}
            onChange={setBase}
            label={sort === 'connector' ? 'Built on connector' : 'Built on'}
            helperText={
              sort === 'connector'
                ? 'Optional. Choose RJ45 to make EthernetPort: every pin is inherited, and you say what each carries in its pinout.'
                : 'Optional. Another port def whose features this one inherits.'
            }
          />

          {sort === 'flow' ? (
            <Box>
              <Typography variant="subtitle2">What crosses it</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                Seen from the part that owns the port. The other side uses the conjugated port (~),
                where every direction here is reversed, so it is stated once.
              </Typography>
              <Table size="small" aria-label="What crosses the port">
                <TableHead>
                  <TableRow>
                    <TableCell>Direction</TableCell>
                    <TableCell>Kind</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell padding="none" />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((row) => (
                    <TableRow key={row.key}>
                      <TableCell sx={{ width: 120 }}>
                        <TextField
                          select
                          size="small"
                          value={row.direction}
                          onChange={(event) => set(row.key, { direction: event.target.value })}
                          slotProps={{ htmlInput: { 'aria-label': 'Direction' } }}
                          fullWidth
                        >
                          <MenuItem value="out">out</MenuItem>
                          <MenuItem value="in">in</MenuItem>
                          <MenuItem value="inout">inout</MenuItem>
                        </TextField>
                      </TableCell>
                      <TableCell sx={{ width: 140 }}>
                        <TextField
                          select
                          size="small"
                          value={row.keyword}
                          onChange={(event) => set(row.key, { keyword: event.target.value, type_name: '' })}
                          slotProps={{ htmlInput: { 'aria-label': 'Kind' } }}
                          fullWidth
                        >
                          <MenuItem value="item">item (a message, a signal)</MenuItem>
                          <MenuItem value="attribute">attribute (a value)</MenuItem>
                        </TextField>
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          value={row.name}
                          onChange={(event) => set(row.key, { name: event.target.value })}
                          placeholder="telemetry"
                          slotProps={{ htmlInput: { 'aria-label': 'Feature name', style: { fontFamily: MONOSPACE } } }}
                          fullWidth
                        />
                      </TableCell>
                      <TableCell sx={{ minWidth: 220 }}>
                        {row.keyword === 'item' ? (
                          <ElementPicker
                            schema={schema}
                            kind="interface"
                            where={(entry) => entry.declared_as === 'message'}
                            valueFor={(entry) => String(entry.qualified_name ?? entry.id)}
                            value={row.type_name || null}
                            onChange={(value) => set(row.key, { type_name: value })}
                            label="Message"
                            ariaLabel="Item type"
                          />
                        ) : (
                          <TextField
                            size="small"
                            value={row.type_name}
                            onChange={(event) => set(row.key, { type_name: event.target.value })}
                            placeholder="ISQ::ElectricPotentialDifferenceValue"
                            slotProps={{ htmlInput: { 'aria-label': 'Attribute type', style: { fontFamily: MONOSPACE } } }}
                            fullWidth
                          />
                        )}
                      </TableCell>
                      <TableCell padding="none">
                        <IconButton
                          size="small"
                          aria-label="Remove this row"
                          onClick={() => setRows((current) => current.filter((entry) => entry.key !== row.key))}
                          disabled={rows.length === 1}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <Button size="small" startIcon={<AddIcon />} onClick={() => setRows((current) => [...current, emptyRow()])}>
                Add a row
              </Button>
            </Box>
          ) : (
            <Stack spacing={2}>
              <TextField
                label="Connector type"
                value={connectorType}
                onChange={(event) => setConnectorType(event.target.value)}
                size="small"
                helperText={inheritsPins
                  ? 'Optional; left empty, the connector it is built on says it'
                  : 'The family and variant, e.g. RJ-45, MIL-DTL-38999 Series III 11-35'}
              />
              {inheritsPins ? (
                <Alert severity="info" icon={false}>
                  Every pin of the connector it is built on is inherited. Assign what each one carries
                  in this port definition&apos;s pinout once it exists.
                </Alert>
              ) : (
                <>
                  <TextField
                    label="Pins"
                    value={pinText}
                    onChange={(event) => setPinText(event.target.value)}
                    size="small"
                    multiline
                    minRows={1}
                    error={Boolean(problem)}
                    helperText={problem || 'A count - 8 gives pin1 to pin8 - or names, one per line or comma separated'}
                    slotProps={{ htmlInput: { style: { fontFamily: MONOSPACE } } }}
                  />
                  {pins.length > 0 && (
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }} aria-label="Pins to be declared">
                      {pins.map((pin) => <Chip key={pin} size="small" label={pin} variant="outlined" />)}
                    </Box>
                  )}
                </>
              )}
            </Stack>
          )}

          <TextField
            label="Description"
            value={statement}
            onChange={(event) => setStatement(event.target.value)}
            multiline
            minRows={2}
            size="small"
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button variant="contained" onClick={() => void submit()} disabled={!ready}>
          {busy ? 'Creating…' : 'Create port definition'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
